import { createSelector } from "reselect";

import { AutoAdjustDateLevel } from "../../constants/autoAdjustDateLevel";
import { immutableRecords } from "../../types";
import { calendarSelectors } from "./calendar";
import { unitScaleForZoomLimit } from "./calendar/dateFunctions/unitScaleFunctions";
import { colorSelectors } from "./colors";

const _getEnabledStuff = stuff => {
  const byId = stuff.byId.filter(s => s.enabled);
  const allIds = stuff.allIds.filter(id => byId.has(id));
  return stuff.merge({
    byId,
    allIds
  });
};

const _getCachedEnabledProperties = createSelector(
  // input
  properties => properties,
  // output
  _getEnabledStuff
);

const _getCachedEnabledReferences = createSelector(
  // input
  references => references,
  // output
  _getEnabledStuff
);

const _getCachedEnabledTypes = createSelector(
  // input
  types => types,
  // output
  _getEnabledStuff
);

const _getCachedDefinitionStuff = createSelector(
  // input
  definitions => _getCachedEnabledProperties(definitions.properties),
  definitions => _getCachedEnabledReferences(definitions.references),
  definitions => _getCachedEnabledTypes(definitions.types),
  // output
  (properties, inReferences, inTypes) => {
    const references = inReferences.update("byId", byId =>
      byId.map(ref => ref.update("types", types => types.filter(t => inTypes.byId.has(t))))
    );
    const types = inTypes.update("byId", byId =>
      byId.map(t =>
        t
          .update("references", refs => refs.filter(r => references.byId.has(r)))
          .update("properties", props => props.filter(p => properties.byId.has(p)))
          .update("superTypes", supers => supers.filter(s => inTypes.byId.has(s)))
      )
    );
    return {
      properties,
      references,
      types
    };
  }
);

const getEnabledDefinitions = createSelector(
  // input
  state => state.definitions,
  // output
  definitions => {
    const filteredStuff = _getCachedDefinitionStuff(definitions);
    return definitions.merge(filteredStuff);
  }
);

const isNarrativeAllowed = state => state.definitions.includeNarrative;

const getDefaultColumnType = state =>
  getEnabledDefinitions(state).types.allIds.find(id =>
    getEnabledDefinitions(state).getIn(["types", "byId", id, "showAsColumn"])
  );

const getDefaultSequenceType = state => {
  let defaultType = getEnabledDefinitions(state).types.allIds.find(
    id =>
      getEnabledDefinitions(state).getIn(["types", "byId", id, "defaultSequenceType"]) &&
      getEnabledDefinitions(state).getIn(["types", "byId", id, "canHaveDate"]) &&
      getEnabledDefinitions(state).getIn(["types", "byId", id, "showInSequence"])
  );
  if (!defaultType) {
    defaultType = getEnabledDefinitions(state).types.allIds.find(
      id =>
        getEnabledDefinitions(state).getIn(["types", "byId", id, "canHaveDate"]) &&
        getEnabledDefinitions(state).getIn(["types", "byId", id, "showInSequence"])
    );
  }
  return defaultType;
};

const getDefaultTimelineType = state => {
  let defaultType = getEnabledDefinitions(state).types.allIds.find(
    id =>
      getEnabledDefinitions(state).getIn(["types", "byId", id, "defaultSequenceType"]) &&
      getEnabledDefinitions(state).getIn(["types", "byId", id, "canHaveDate"]) &&
      getEnabledDefinitions(state).timelineViewSettings.showTypes.includes(id)
  );
  if (!defaultType) {
    defaultType = getEnabledDefinitions(state).types.allIds.find(
      id =>
        getEnabledDefinitions(state).getIn(["types", "byId", id, "canHaveDate"]) &&
        getEnabledDefinitions(state).timelineViewSettings.showTypes.includes(id)
    );
  }
  return defaultType;
};

const getDefaultNarrativeFolderType = state => {
  let defaultType = getEnabledDefinitions(state).types.allIds.find(
    id =>
      getEnabledDefinitions(state).getIn(["types", "byId", id, "canIncludeInNarrative"]) &&
      getEnabledDefinitions(state).getIn(["types", "byId", id, "isNarrativeFolder"])
  );
  return defaultType;
};

const getDefaultNarrativeDocumentType = state => {
  let defaultType = getEnabledDefinitions(state).types.allIds.find(
    id =>
      getEnabledDefinitions(state).getIn(["types", "byId", id, "defaultNarrativeType"]) &&
      getEnabledDefinitions(state).getIn(["types", "byId", id, "canIncludeInNarrative"]) &&
      !getEnabledDefinitions(state).getIn(["types", "byId", id, "isNarrativeFolder"])
  );
  if (!defaultType) {
    defaultType = getEnabledDefinitions(state).types.allIds.find(
      id =>
        getEnabledDefinitions(state).getIn(["types", "byId", id, "canIncludeInNarrative"]) &&
        !getEnabledDefinitions(state).getIn(["types", "byId", id, "isNarrativeFolder"])
    );
  }
  return defaultType;
};

const getReferencesById = state => getEnabledDefinitions(state).references.byId;
const getReferenceIds = state => getEnabledDefinitions(state).references.allIds;
const getReferenceLabel = (referencesById, id) => referencesById.getIn([id, "label"]);
const getReferenceIcon = (referencesById, id) => referencesById.getIn([id, "icon"]);
const getReferenceColor = (referencesById, id) => referencesById.getIn([id, "color"]);
const getReferenceInverseLabel = (referencesById, id) => {
  const ref = referencesById.get(id);
  return ref.inverseLabel && !ref.bidirectional ? ref.inverseLabel : ref.label;
};

const getDefaultUnitScaleWithParams = (calendar, zoomFrom, zoomTo) => {
  const minUnitScale = unitScaleForZoomLimit(calendar, zoomTo, false);
  const maxUnitScale = unitScaleForZoomLimit(calendar, zoomFrom, true);
  return (maxUnitScale + 100 * minUnitScale) / 101;
};

const getDefaultUnitScale = state => {
  const calendar = calendarSelectors.getCalendar(state);
  const zoomFrom = state.definitions.timelineViewSettings.zoomFrom;
  const zoomTo = state.definitions.timelineViewSettings.zoomTo;
  return getDefaultUnitScaleWithParams(calendar, zoomFrom, zoomTo);
};

const getDefaultColorId = state => state.definitions.colors.allIds.get(0);

const getColorMap = state => state.definitions.colors.byId;

const getSwatchColorByColorId = (state, colorId) => {
  const color = getColorMap(state).get(colorId);
  return color ? color.swatchColor : null;
};

const getTimelineViewSettings = state => state.definitions.timelineViewSettings;
const getNarrativeViewSettings = state => state.definitions.narrativeViewSettings;
const getNarrativeSettings = state => state.definitions.narrativeSettings;
const getRelationshipViewSettings = state => state.definitions.relationshipViewSettings;
const getMindmapViewSettings = state => state.definitions.mindmapViewSettings;
const getSubwayViewSettings = state => state.definitions.subwayViewSettings;
const getMobileItemCardViewSettings = state => state.definitions.mobileItemCardViewSettings;

// Get the list of available colors for the inspector dropdown
const getColorOptionsFromColorMap = createSelector(
  // fetch parameters for last calculate function
  colors => colors,
  // Calculate result
  colors =>
    colors
      .map((color, _id) =>
        immutableRecords.DropdownOptionRecord({
          label: color.label,
          value: color.id,
          color: color.swatchColor
        })
      )
      .toList()
);
const getColorOptions = state => getColorOptionsFromColorMap(colorSelectors.getColors(state));

const getTypesById = state => getEnabledDefinitions(state).types.byId;
const getTypeAllIds = state => getEnabledDefinitions(state).types.allIds;

const getPropertiesById = state => getEnabledDefinitions(state).properties.byId;

// /**
//  * Work out what the label should be for any given ID
//  * @param {TimelineDefinitionsRecord} definitions
//  * @param {string} id
//  */
// const getDefinitionLabelForId = (definitions, id) => {

// };
// const getAdjustItemEndWhenChangingStart = state => state.definitions.adjustItemEndWhenChangingStart;
const getAdjustItemEndWhenChangingStart = state => state.definitions.autoAdjustLevel >= AutoAdjustDateLevel.AdjustSelf;

// const getMoveChildItemsWithParent = state => state.definitions.moveChildItemsWithParent;
const getMoveChildItemsWithParent = state => state.definitions.autoAdjustLevel >= AutoAdjustDateLevel.MoveChildren;

const getAutoAdjustLevel = state => state.definitions.autoAdjustLevel;
const getAutoAdjustBackwardsLimit = state => state.definitions.autoAdjustBackwardsLimit;

const getStoreRelativePaths = state => state.definitions.storeRelativePaths;

const getOverrideNowTimestamp = state => state.definitions.overrideNowTimestamp;

export default {
  getEnabledDefinitions,
  isNarrativeAllowed,
  getTypesById,
  getTypeAllIds,
  getDefaultColumnType,
  getDefaultSequenceType,
  getDefaultTimelineType,
  getDefaultNarrativeDocumentType,
  getDefaultNarrativeFolderType,
  getReferencesById,
  getReferenceIds,
  getReferenceLabel,
  getReferenceInverseLabel,
  getReferenceIcon,
  getReferenceColor,
  getDefaultUnitScaleWithParams,
  getDefaultUnitScale,
  getColorMap,
  getSwatchColorByColorId,
  getDefaultColorId,
  getTimelineViewSettings,
  getNarrativeViewSettings,
  getNarrativeSettings,
  getRelationshipViewSettings,
  getMindmapViewSettings,
  getSubwayViewSettings,
  getMobileItemCardViewSettings,
  getPropertiesById,

  getAutoAdjustLevel,
  getAutoAdjustBackwardsLimit,
  getAdjustItemEndWhenChangingStart,
  getMoveChildItemsWithParent,

  getStoreRelativePaths,

  getOverrideNowTimestamp,

  // Gets options for dropdown lists
  getColorOptions,
  getColorOptionsFromColorMap
};
